import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { T } from '../theme/tokens';

/** One column of a right-panel sortable grid. `sortVal` returns the value the column sorts on
 *  (blanks — null/'' — always sort LAST regardless of direction); omit it to make the column
 *  unsortable. `render` draws the cell. `frozen` pins a leading identity column so it stays put
 *  while the rest of the grid scrolls horizontally (only contiguous leading columns may freeze).
 *  Columns auto-size to their widest value / header (capped); a drag-resize overrides that. */
export interface PanelCol<Row> {
  key: string;
  header: string;
  width: number;                 // legacy width hint — kept for source compat; auto-width drives layout
  grow?: boolean;                // legacy — ignored now that columns size to content
  align?: 'left' | 'right';
  sortable?: boolean;
  frozen?: boolean;              // leading column stays pinned during horizontal scroll
  sortVal?: (r: Row) => string | number | null;
  render: (r: Row) => React.ReactNode;
  /** Clean value for an Excel/CSV export of this column (the underlying datum, not the React cell).
   *  `exportKind` tells the workbook how to type/format it (date serial, currency, percent, link). */
  exportVal?: (r: Row) => string | number | null;
  exportKind?: 'text' | 'date' | 'money' | 'percent' | 'url';
}

type Sort = { key: string; dir: 'asc' | 'desc' };

// Cap so one long value (a wordy status, a multi-token cell) can't blow a column out to the panel
// width — it truncates with an ellipsis instead, and the value stays readable on hover via title.
const CAP = 280;
const BAR_KEY = '__leftbar__';

/** Direction-aware comparator: blanks (null / '') ALWAYS sort last (never flipped to the top by
 *  a descending sort); among real values, numbers compare numeric, strings natural (numeric-aware),
 *  then `dir` (+1 asc / -1 desc) is applied only to the real-vs-real result. */
const cmp = (a: string | number | null, b: string | number | null, dir: number): number => {
  const an = a == null || a === '', bn = b == null || b === '';
  if (an && bn) return 0;
  if (an) return 1;   // a is blank → after b, regardless of dir
  if (bn) return -1;  // b is blank → after a, regardless of dir
  const c = (typeof a === 'number' && typeof b === 'number')
    ? a - b : String(a).localeCompare(String(b), undefined, { numeric: true });
  return c * dir;
};

/** A compact, sortable grid for the right rail — a real <table> so columns auto-size to their
 *  widest value/header and the whole grid scrolls horizontally when it outgrows the panel, with the
 *  leading identity column(s) frozen in place. Sticky clickable headers (click to sort, click again
 *  to flip ▲/▼), zebra rows, optional coloured left rail. It does NOT own scrolling: the caller
 *  wraps it in a scroll container, so one component serves every right-panel unit grid (Build Order
 *  sequence + Units-in-view) and any future ones. */
export function PanelGrid<Row>({ rows, cols, rowKey, defaultSort, onRowClick, onRowHover, selectedKey, leftBar, emptyText, rowTitle, groupOf, groupId, groupHeader, orderedRowsRef, colOrder, onReorder }: {
  rows: Row[];
  cols: PanelCol<Row>[];
  rowKey: (r: Row) => string;
  defaultSort: Sort;
  onRowClick?: (r: Row) => void;
  onRowHover?: (r: Row | null) => void;
  selectedKey?: string | null;
  /** optional coloured rail per row (e.g. build-order heat / colour-by swatch); null → no bar */
  leftBar?: (r: Row) => string | null;
  emptyText?: string;
  rowTitle?: (r: Row) => string;
  /** Optional grouping: `groupOf` bands rows under collapsible headers (default collapsed). `groupId`
   *  is the grouping dimension's identity — collapse state resets when it changes. `groupHeader`
   *  renders the header content (label + a colour dot); falls back to the raw key + count. */
  /** Group key(s) for a row. Return an ARRAY for multi-value dimensions (e.g. construction upgrades)
   *  so a row bands under EACH of its values — a unit with two upgrades appears in both groups. */
  groupOf?: (r: Row) => string | string[];
  groupId?: string;
  groupHeader?: (key: string, count: number) => React.ReactNode;
  /** Mirror of the current sorted row order (each row once, ignoring group bands) so a caller can
   *  export exactly what the grid shows in the order it shows it. Updated after each re-sort. */
  orderedRowsRef?: React.MutableRefObject<Row[]>;
  /** Drag-to-reorder columns: `colOrder` is the saved key order applied to the NON-frozen columns
   *  (frozen identity columns always stay leading); unknown keys (e.g. a fresh lens column) append in
   *  natural order. `onReorder` fires with the new non-frozen key order when a header is dropped. */
  colOrder?: string[];
  onReorder?: (orderedKeys: string[]) => void;
}) {
  const [sort, setSort] = useState<Sort>(defaultSort);
  // Collapsible groups — `expanded` holds only the OPEN keys (default = all collapsed). Resets to
  // all-collapsed whenever the grouping dimension (`groupId`) changes, so the list starts tidy.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  useEffect(() => { setExpanded(new Set()); }, [groupId]);
  const toggleGroup = (k: string) => setExpanded(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  // Per-column width OVERRIDES (drag the header divider to resize). A resized column locks to that
  // px width; un-set columns keep auto-sizing to their content.
  const [colW, setColW] = useState<Record<string, number>>({});
  // Measured sticky-left offsets for the frozen leading columns (so a 2nd frozen column pins right
  // after the 1st, whatever the 1st's auto width turns out to be). Keyed by column key (+ BAR_KEY).
  const [lefts, setLefts] = useState<Record<string, number>>({});
  const headRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const tableRef = useRef<HTMLTableElement | null>(null);

  // Apply the saved drag-reorder: frozen identity columns always lead (in their natural order); the
  // rest follow `colOrder`, with any key not in the saved order appended in natural order. Drives
  // both the header and body so the whole grid follows the user's chosen column order.
  const ocols = useMemo(() => {
    if (!colOrder || !colOrder.length) return cols;
    const idx = new Map(colOrder.map((k, i) => [k, i] as const));
    const frozen = cols.filter(c => c.frozen);
    const rest = cols.filter(c => !c.frozen)
      .map((c, i) => ({ c, i }))
      .sort((a, b) => ((idx.has(a.c.key) ? idx.get(a.c.key)! : Infinity) - (idx.has(b.c.key) ? idx.get(b.c.key)! : Infinity)) || (a.i - b.i))
      .map(x => x.c);
    return [...frozen, ...rest];
  }, [cols, colOrder]);

  // Column drag-reorder state (header drag-and-drop). `dragKey` = the column being dragged; `overKey`
  // = the column it's hovering, which shows a drop indicator.
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const reorder = (from: string, to: string) => {
    if (!onReorder || from === to) return;
    const keys = ocols.filter(c => !c.frozen).map(c => c.key);
    const fi = keys.indexOf(from); if (fi < 0) return;
    keys.splice(fi, 1);
    const ti = keys.indexOf(to); if (ti < 0) return;
    keys.splice(ti, 0, from);   // drop BEFORE the target column
    onReorder(keys);
  };

  const startResize = (key: string, startW: number) => (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    try { (e.target as HTMLElement).setPointerCapture(e.pointerId); } catch { /* noop */ }
    const move = (ev: PointerEvent) => setColW(w => ({ ...w, [key]: Math.max(36, Math.round(startW + (ev.clientX - startX))) }));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  const sorted = useMemo(() => {
    const col = cols.find(c => c.key === sort.key);
    if (!col?.sortVal) return rows;
    const f = col.sortVal;
    const dir = sort.dir === 'asc' ? 1 : -1;
    // Tie-break by rowKey (stable across live re-syncs that reorder equal rows).
    return [...rows].sort((a, b) => cmp(f(a), f(b), dir) || String(rowKey(a)).localeCompare(String(rowKey(b))));
  }, [rows, sort, cols, rowKey]);

  // Keep the caller's export ref in step with the visible sort order (each row once).
  useEffect(() => { if (orderedRowsRef) orderedRowsRef.current = sorted; }, [sorted, orderedRowsRef]);

  // Grouped view: band the (already-sorted) rows under their group key, groups in first-appearance
  // order. null when no grouping → flat list.
  const grouped = useMemo(() => {
    if (!groupOf) return null;
    const m = new Map<string, Row[]>();
    for (const r of sorted) {
      const g = groupOf(r);
      const keys = Array.isArray(g) ? (g.length ? g : ['—']) : [g || '—'];
      for (const k of keys) (m.get(k) || m.set(k, []).get(k)!).push(r);   // a row can land in several groups
    }
    return [...m.entries()].map(([key, rs]) => ({ key, rows: rs }));
  }, [sorted, groupOf]);

  // Frozen = the contiguous leading run of `frozen` columns (the left rail, when present, pins too).
  const frozenKeys = useMemo(() => {
    const ks: string[] = [];
    if (leftBar) ks.push(BAR_KEY);
    for (const c of ocols) { if (c.frozen) ks.push(c.key); else break; }
    return ks;
  }, [ocols, leftBar]);
  const isFrozen = (key: string) => frozenKeys.includes(key);
  const lastFrozen = frozenKeys[frozenKeys.length - 1];

  // After layout, derive each frozen leading cell's sticky `left` by ACCUMULATING the widths of the
  // frozen cells before it (left rail → 1st frozen col → 2nd …). Width is invariant under the sticky
  // translation, so this stays correct no matter how far the grid is scrolled. (Measuring rect.left
  // instead would read a *stuck* cell's translated x and, on any re-measure while scrolled right,
  // pollute the offset by the scroll distance — which flung the frozen Lot column to the far edge.)
  // With borderSpacing:0 the running width sum equals the column's left edge. Body cells reuse these
  // header offsets so a column's header + cells pin to the exact same x.
  useLayoutEffect(() => {
    const next: Record<string, number> = {};
    let acc = 0;
    for (const k of frozenKeys) {
      next[k] = Math.round(acc);
      const el = headRefs.current[k];
      if (el) acc += el.getBoundingClientRect().width;
    }
    setLefts(prev => {
      const same = Object.keys(prev).length === Object.keys(next).length && frozenKeys.every(k => prev[k] === next[k]);
      return same ? prev : next;
    });
  }, [frozenKeys, ocols, colW, sorted]);

  const onHead = (c: PanelCol<Row>) => {
    if (!c.sortable) return;
    setSort(s => s.key === c.key ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: 'asc' });
  };

  // The capped / resized inner box every cell's content sits in (inline-block so the td's text-align
  // positions it; the box clips overflow to an ellipsis).
  const innerStyle = (c: PanelCol<Row>): React.CSSProperties => {
    const fixed = colW[c.key];
    return { display: 'inline-block', verticalAlign: 'middle', maxWidth: CAP,
      ...(fixed != null ? { width: fixed } : null),
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  };

  // Sticky positioning for a frozen leading cell — pins it to the measured x within the scroll
  // container. background:inherit takes the row's opaque zebra/selected/hover colour so scrolled
  // cells slide under it and the pinned column always matches its row.
  const stickyCell = (key: string, z: number): React.CSSProperties =>
    isFrozen(key)
      ? { position: 'sticky', left: lefts[key] ?? 0, zIndex: z, backgroundColor: 'inherit',
          // A soft divider on the rightmost frozen column marks the freeze boundary as the rest
          // scrolls under it.
          ...(key === lastFrozen ? { boxShadow: `1px 0 0 ${T.line}, 4px 0 6px -4px rgba(0,0,0,.18)` } : null) }
      : {};

  const colCount = ocols.length + (leftBar ? 1 : 0) + 1; // + left rail + trailing spacer

  // One data row — shared by the flat list and the grouped view (so columns/frozen/zebra behave
  // identically in both). `i` is the row's index WITHIN its group when grouped (zebra restarts).
  const renderRow = (r: Row, i: number) => {
    const k = rowKey(r);
    const sel = selectedKey != null && k === selectedKey;
    const bg = sel ? T.accentTint : (i % 2 ? T.sand : T.paper);
    const click = onRowClick ? () => onRowClick(r) : undefined;
    return (
      <tr key={k} className={`lfe-pg-row${sel ? ' sel' : ''}`}
        onClick={click}
        onMouseEnter={onRowHover ? () => onRowHover(r) : undefined}
        onMouseLeave={onRowHover ? () => onRowHover(null) : undefined}
        onKeyDown={click ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); click(); } } : undefined}
        role={click ? 'button' : undefined} tabIndex={click ? 0 : undefined}
        title={rowTitle ? rowTitle(r) : undefined}
        style={{ background: bg, cursor: click ? 'pointer' : 'default' }}>
        {leftBar && (
          <td style={{ ...stickyCell(BAR_KEY, 2), padding: '0 3px' }}>
            <span aria-hidden style={{ display: 'block', width: 4, height: 18, borderRadius: 2, background: leftBar(r) || 'transparent' }} />
          </td>
        )}
        {ocols.map(c => (
          <td key={c.key} style={{ ...stickyCell(c.key, 2), padding: '6px 8px', textAlign: c.align || 'left' }}>
            <span style={innerStyle(c)}>{c.render(r)}</span>
          </td>
        ))}
        <td aria-hidden />
      </tr>
    );
  };

  return (
    <table ref={tableRef} style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', tableLayout: 'auto', fontSize: 13 }}>
      <thead>
        <tr>
          {leftBar && (
            <th ref={el => { headRefs.current[BAR_KEY] = el; }} aria-hidden
              style={{ position: 'sticky', top: 0, left: lefts[BAR_KEY] ?? 0, zIndex: 4, background: T.sand,
                padding: 0, width: 10, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }} />
          )}
          {ocols.map((c, ci) => {
            const active = sort.key === c.key;
            const froz = isFrozen(c.key);
            // Non-frozen headers are drag-reorderable when the caller wired onReorder.
            const drag = !!onReorder && !froz;
            const isDragging = dragKey === c.key;
            const isOver = overKey === c.key && dragKey !== c.key;
            return (
              // Header cell is sticky to the TOP (and, when frozen, also to the LEFT) so it pins as
              // both axes scroll. position:relative would break sticky, so the resize grip is placed
              // absolutely inside this sticky cell instead.
              <th key={c.key} ref={el => { headRefs.current[c.key] = el; }}
                draggable={drag}
                onDragStart={drag ? e => { setDragKey(c.key); try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', c.key); } catch { /* noop */ } } : undefined}
                onDragOver={drag ? e => { if (dragKey && dragKey !== c.key) { e.preventDefault(); try { e.dataTransfer.dropEffect = 'move'; } catch { /* noop */ } if (overKey !== c.key) setOverKey(c.key); } } : undefined}
                onDragLeave={drag ? () => setOverKey(k => (k === c.key ? null : k)) : undefined}
                onDrop={drag ? e => { e.preventDefault(); if (dragKey) reorder(dragKey, c.key); setDragKey(null); setOverKey(null); } : undefined}
                onDragEnd={drag ? () => { setDragKey(null); setOverKey(null); } : undefined}
                style={{ position: 'sticky', top: 0, zIndex: froz ? 4 : 3, ...(froz ? { left: lefts[c.key] ?? 0 } : null),
                  background: T.sand, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
                  // Carry the freeze divider onto the header too, so the boundary is continuous top-to-bottom.
                  ...(c.key === lastFrozen ? { boxShadow: `1px 0 0 ${T.line}, 4px 0 6px -4px rgba(0,0,0,.18)` } : null),
                  // Drop indicator: a left accent edge on the column being hovered; dim the dragged one.
                  ...(isOver ? { boxShadow: `inset 3px 0 0 ${T.accent}` } : null),
                  opacity: isDragging ? 0.45 : 1,
                  padding: '5px 8px', textAlign: c.align || 'left', whiteSpace: 'nowrap' }}>
                <button onClick={e => { onHead(c); if (e.detail) (e.currentTarget as HTMLButtonElement).blur(); }} disabled={!c.sortable} className="lfe-press"
                  title={c.sortable ? `Sort by ${c.header}${drag ? ' · drag to reorder' : ''}` : (drag ? `${c.header} · drag to reorder` : c.header)}
                  style={{ position: 'relative', margin: 0, border: 'none', background: 'transparent', padding: 0,
                    cursor: c.sortable ? 'pointer' : (drag ? 'grab' : 'default'), color: active ? T.maroon : T.inkSoft,
                    fontWeight: 700, letterSpacing: '.04em', fontSize: 10, display: 'inline-flex', alignItems: 'center',
                    gap: 2, maxWidth: '100%', justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.header.toUpperCase()}</span>
                  <span aria-hidden style={{ flex: '0 0 auto', opacity: active ? 1 : 0 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>
                </button>
                {ci < ocols.length - 1 && (
                  // Visible drag affordance: an 11px-wide invisible hit zone with a thin centered
                  // divider inside it, so the column boundary reads at a glance and darkens on hover.
                  // draggable=false + stop dragstart so grabbing the divider resizes (never reorders).
                  <span role="separator" aria-orientation="vertical" title="Drag to resize column" className="lfe-colsep" draggable={false}
                    onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                    onPointerDown={startResize(c.key, headRefs.current[c.key]?.offsetWidth ?? c.width)} onClick={e => e.stopPropagation()}
                    style={{ position: 'absolute', top: 0, bottom: 0, right: -2, width: 11, cursor: 'col-resize',
                      touchAction: 'none', display: 'flex', justifyContent: 'center', zIndex: 1 }}>
                    <span aria-hidden className="lfe-colsep-line"
                      style={{ width: 2, height: '64%', alignSelf: 'center', borderRadius: 1, background: T.sand2 }} />
                  </span>
                )}
              </th>
            );
          })}
          {/* Trailing spacer column eats leftover width so real columns stay content-sized and the
              grid still fills the panel when narrow; it collapses to 0 once the grid overflows. */}
          <th aria-hidden style={{ position: 'sticky', top: 0, zIndex: 3, width: '100%', background: T.sand, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`, padding: 0 }} />
        </tr>
      </thead>
      <tbody>
        {sorted.length === 0
          ? <tr><td colSpan={colCount} style={{ fontSize: 12.5, color: T.inkSoft, padding: '10px 12px' }}>{emptyText || 'No units in this selection.'}</td></tr>
          : grouped
            ? grouped.map(g => {
                const open = expanded.has(g.key);
                return (
                  <React.Fragment key={'g:' + g.key}>
                    {/* Collapsible group band — pinned left so the label stays readable during
                        horizontal scroll; click toggles the group (default collapsed). */}
                    <tr className="lfe-press" onClick={() => toggleGroup(g.key)} style={{ cursor: 'pointer' }}>
                      <td colSpan={colCount} style={{ position: 'sticky', left: 0, background: T.sand2,
                        borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`, padding: '5px 10px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 700, color: T.maroon, letterSpacing: '.03em' }}>
                          <span aria-hidden style={{ fontSize: 9, color: T.inkSoft, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', display: 'inline-block', width: 10, textAlign: 'center' }}>▶</span>
                          {groupHeader ? groupHeader(g.key, g.rows.length)
                            : <>{g.key}<span className="num" style={{ marginLeft: 6, color: T.inkSoft, fontWeight: 600 }}>{g.rows.length}</span></>}
                        </span>
                      </td>
                    </tr>
                    {open && g.rows.map((r, i) => renderRow(r, i))}
                  </React.Fragment>
                );
              })
            : sorted.map((r, i) => renderRow(r, i))}
      </tbody>
    </table>
  );
}

/** Shared Build-Stage cell: a tinted pill coloured by the build-status lens (truncates in-cell). */
export function StagePill({ stage, color }: { stage: string; color: string }) {
  if (!stage) return <span style={{ color: T.inkSoft }}>—</span>;
  return (
    <span style={{ fontSize: 10.5, fontWeight: 600, color: T.ink, background: `${color}26`,
      padding: '2px 8px', borderRadius: 999, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}>
      {stage}
    </span>
  );
}

/** Shared % Complete cell — green at 100%, dash when untracked. */
export function PctCell({ pct }: { pct: number | null }) {
  if (pct == null) return <span style={{ color: T.inkSoft }}>—</span>;
  const pc = Math.round((pct <= 1 ? pct * 100 : pct));
  return <span className="num" style={{ fontSize: 11, fontWeight: 600, color: pc >= 100 ? '#2a9d3e' : T.ink }}>{pc}%</span>;
}
